import ky from 'ky'

// ─── Promos API (supermarche-promos Worker) ───────────────────────────────────
// These routes are served by the same Worker at /api/* paths.
export const promosApi = {
  getAll: (date) => ky.get('/api/promos', { searchParams: date ? { date } : {} }).json(),
  trigger: (params = {}) => ky.post('/api/trigger', { json: params }).json(),
  health: () => ky.get('/api/health').json(),
}

// ─── Meal planning API (betina backend) ───────────────────────────────────────
// Default user ID — in a real app this would come from auth.
// Hardcoded to "greg" until household auth is wired up.
const getUserId = () => localStorage.getItem('betina_user_id') ?? 'greg'

export const api = ky.create({
  prefixUrl: '/api',
  headers: {
    'content-type': 'application/json',
  },
  hooks: {
    beforeRequest: [
      (request) => {
        request.headers.set('X-User-Id', getUserId())
      },
    ],
  },
})

// ─── Convenience helpers ──────────────────────────────────────────────────────

export const stockApi = {
  list: (location) => api.get('stock', { searchParams: location ? { userId: getUserId(), location } : { userId: getUserId() } }).json(),
  create: (item) => api.post('stock', { json: { ...item, userId: getUserId() } }).json(),
  update: (id, fields) => api.patch(`stock/${id}`, { json: fields }).json(),
  delete: (id) => api.delete(`stock/${id}`).json(),
  bulkCreate: (items) => api.post('stock/bulk', { json: { userId: getUserId(), items } }).json(),
}

export const recipesApi = {
  list: (params) => api.get('recipes', { searchParams: params }).json(),
  get: (id) => api.get(`recipes/${id}`).json(),
  create: (recipe) => api.post('recipes', { json: recipe }).json(),
  addReview: (id, review) => api.post(`recipes/${id}/reviews`, { json: { ...review, userId: getUserId() } }).json(),
}

export const mealPlanApi = {
  get: (weekStart) => api.get('meal-plans', { searchParams: { userId: getUserId(), weekStart } }).json(),
  listRecent: () => api.get('meal-plans', { searchParams: { userId: getUserId() } }).json(),
  create: (weekStart) => api.post('meal-plans', { json: { userId: getUserId(), weekStart } }).json(),
  addSlot: (planId, slot) => api.post(`meal-plans/${planId}/slots`, { json: slot }).json(),
  updateSlotStatus: (slotId, status) => api.patch(`meal-plans/slots/${slotId}/status`, { json: { status } }).json(),
  deleteSlot: (slotId) => api.delete(`meal-plans/slots/${slotId}`).json(),
  moveSlot: (planId, payload) => api.post(`meal-plans/${planId}/move`, { json: payload }).json(),
}

export const shoppingApi = {
  list: () => api.get('shopping', { searchParams: { userId: getUserId() } }).json(),
  get: (id) => api.get(`shopping/${id}`).json(),
  create: (data) => api.post('shopping', { json: { ...data, userId: getUserId() } }).json(),
  addItem: (listId, item) => api.post(`shopping/${listId}/items`, { json: item }).json(),
  bulkAddItems: (listId, items) => api.post(`shopping/${listId}/items/bulk`, { json: { items } }).json(),
  toggleItem: (itemId, isChecked) => api.patch(`shopping/items/${itemId}/toggle`, { json: { isChecked } }).json(),
  updateStatus: (id, status) => api.patch(`shopping/${id}/status`, { json: { status } }).json(),
}

export const dishLogApi = {
  list: (limit = 20) => api.get('dish-logs', { searchParams: { userId: getUserId(), limit } }).json(),
  create: (log) => api.post('dish-logs', { json: { ...log, userId: getUserId() } }).json(),
}

export const aiApi = {
  suggestMeals: (data) => api.post('ai/suggest-meals', { json: { ...data, userId: getUserId() }, timeout: 60000 }).json(),
  analyzeFridge: (data) => api.post('ai/analyze-fridge', { json: { ...data, userId: getUserId() }, timeout: 60000 }).json(),
  analyzeReceipt: (data) => api.post('ai/analyze-receipt', { json: { ...data, userId: getUserId() }, timeout: 60000 }).json(),
  suggestFromStock: (mealType) => api.post('ai/suggest-from-stock', { json: { userId: getUserId(), mealType }, timeout: 60000 }).json(),
  shoppingList: ({ weekStart }) => api.post('ai/shopping-list', { json: { weekStart, userId: getUserId() }, timeout: 60000 }).json(),
}

export const dishesApi = {
  list: (limit = 30) => api.get('dishes', { searchParams: { userId: getUserId(), limit } }).json(),
  save: (dish) => api.post('dishes', { json: { ...dish, userId: getUserId() } }).json(),
  delete: (id) => api.delete(`dishes/${id}`).json(),
}

export const constraintsApi = {
  get: () => api.get('constraints', { searchParams: { userId: getUserId() } }).json(),
  set: (constraints) => api.put('constraints', { json: { constraints, userId: getUserId() } }).json(),
}

export const marmitonApi = {
  getSuggestions: () => api.get('marmiton/suggestions', { timeout: 15000 }).json(),
}

export const weeklyReviewApi = {
  submit: ({ weekStart, prepared }) =>
    api
      .post('meal-plans/weekly-review', {
        json: { userId: getUserId(), weekStart, prepared },
      })
      .json(),
}

// ─── Identification (lier la session web à un foyer WhatsApp) ────────────────
export const identityApi = {
  identify: (phone) => ky.post('/api/identify', { json: { phone } }).json(),
}
export function setUserId(id) { localStorage.setItem('betina_user_id', id) }
export function clearUserId() { localStorage.removeItem('betina_user_id') }
export function currentUserId() { return localStorage.getItem('betina_user_id') }

// ─── Household API ────────────────────────────────────────────────────────────
export const householdApi = {
  get: () => api.get('household').json(),
  addMember: (member) => api.post('household/members', { json: member }).json(),
  updateMember: (id, fields) => api.patch(`household/members/${id}`, { json: fields }).json(),
  removeMember: (id) => api.delete(`household/members/${id}`).json(),
  setAllergies: (id, allergies) =>
    api.put(`household/members/${id}/allergies`, { json: { allergies } }).json(),
  setPreferences: (id, preferences) =>
    api.put(`household/members/${id}/preferences`, { json: { preferences } }).json(),
}
